import React, { useEffect } from 'react';
import { ChevronRight, More } from '@wix/wix-ui-icons-common';

import { PopoverMenu } from '../PopoverMenu';
import { PopoverTriggerElementProps } from '../PopoverMenu/PopoverMenu.types';
import { ButtonPriority, ButtonProps } from '../Button/Button.types';
import { Button } from '../Button';
import { IconButton } from '../IconButton';
import { Tooltip } from '../Tooltip';
import { TooltipChildrenProps } from '../Tooltip/Tooltip.types';
import { DATA_HOOKS } from './constants';
import { HoverSlot } from './HoverSlot';
import { st, classes } from './TableActionCell.st.css';
import { deprecationLog } from '../utils/deprecationLog';
import { useIcons } from '../WixDesignSystemIconThemeProvider';
import { useDefaultProps } from '../WixDesignSystemDefaultPropsProvider';
import type {
  TableActionCellProps,
  TableActionCellPrimaryAction,
  TableActionCellMainAction,
  TableActionCellSecondaryAction,
  TableActionCellSecondaryActionsProps,
  TableActionCellSecondaryDivider,
  TableActionCellSize,
  TableActionCellMoreActionsProps,
  TableActionCellPopoverMenuProps,
} from './TableActionCell.types';

type TableActionCellIcons = {
  ChevronRight: React.ComponentType;
  More: React.ComponentType;
};

type PrimaryActionButtonConfig = {
  dataHook?: string;
  text: string;
  skin?: string;
  onClick: () => void;
  disabled?: boolean;
  prefixIcon?: React.ReactElement;
  suffixIcon?: React.ReactElement;
  as?: React.ElementType;
  priority?: string;
  visibility?: string;
  disabledDescription?: string;
  tooltipProps?: import('../common').TooltipCommonProps;
  [key: string]: unknown;
};

const blurElementWithoutFocusVisible = (element: HTMLElement) => {
  if (process.env.NODE_ENV !== 'test' && !element.matches(':focus-visible')) {
    const activeElement = document.activeElement as HTMLElement | null;
    if (activeElement && typeof activeElement.blur === 'function') {
      activeElement.blur();
    }
  }
};

const renderMainActionButton = (
  {
    dataHook,
    text,
    skin,
    onClick,
    disabled,
    prefixIcon,
    suffixIcon,
    as,
    priority,
    visibility,
    disabledDescription,
    tooltipProps,
    ...rest
  }: PrimaryActionButtonConfig,
  size: TableActionCellSize,
) => {
  const visibilityPriorityMap: Record<string, ButtonPriority> = {
    always: 'secondary',
    onHover: 'primary',
  };
  const isDisabledWithTooltip = disabled && !!disabledDescription;

  function renderButton() {
    const buttonProps: Record<string, unknown> = {
      key: text,
      as,
      dataHook: dataHook || DATA_HOOKS.primaryAction,
      className: st(classes.action, { size }),
      disabled,
      skin,
      priority:
        (visibility ? visibilityPriorityMap[visibility] : undefined) ??
        priority,
      size,
      prefixIcon,
      suffixIcon,
      onClick: (event: React.MouseEvent) => {
        onClick();
        blurElementWithoutFocusVisible(event.target as HTMLElement);

        event.stopPropagation();
      },
      ...rest,
    };
    return <Button {...(buttonProps as ButtonProps)}>{text}</Button>;
  }

  if (isDisabledWithTooltip) {
    return (
      <Tooltip
        dataHook={DATA_HOOKS.primaryActionTooltip}
        disabled={false}
        content={disabledDescription}
        {...tooltipProps}
      >
        {renderButton()}
      </Tooltip>
    );
  }

  return renderButton();
};

const renderVisibleActions = (
  actions: TableActionCellSecondaryAction[],
  size: TableActionCellSize,
  secondaryActionsProps: TableActionCellSecondaryActionsProps | undefined,
) =>
  actions.map(
    (
      {
        text,
        icon,
        onClick,
        dataHook,
        disabled,
        disabledDescription,
        tooltipProps,
        skin,
      },
      index,
    ) => (
      <Tooltip
        key={index}
        dataHook={dataHook || DATA_HOOKS.visibleActionTooltip}
        disabled={disabled && disabledDescription === ''}
        content={
          disabled && Boolean(disabledDescription) ? disabledDescription : text
        }
        {...tooltipProps}
      >
        <IconButton
          dataHook={DATA_HOOKS.visibleAction}
          priority={secondaryActionsProps?.priority || 'secondary'}
          disabled={disabled}
          className={st(classes.action, { size, iconAction: true })}
          onClick={(event: React.MouseEvent<HTMLElement>) => {
            onClick(event);
            blurElementWithoutFocusVisible(event.target as HTMLElement);

            event.stopPropagation();
          }}
          size={size}
          ariaLabel={text}
          skin={skin}
        >
          {icon}
        </IconButton>
      </Tooltip>
    ),
  );

const renderHiddenActions = (
  actions: (TableActionCellSecondaryAction | TableActionCellSecondaryDivider)[],
  popoverMenuProps: TableActionCellPopoverMenuProps | undefined,
  size: TableActionCellSize,
  moreActionsTooltipText: string | undefined,
  moreActionsProps: TableActionCellMoreActionsProps | undefined,
  icons: TableActionCellIcons,
) => (
  <PopoverMenu
    dataHook={DATA_HOOKS.popoverMenu}
    appendTo="window"
    placement="top"
    textSize="small"
    triggerElement={({ toggle, isOpen, ref }: PopoverTriggerElementProps) => (
      <Tooltip
        content={moreActionsTooltipText}
        disabled={!moreActionsTooltipText}
      >
        {({ onFocus, onBlur }: TooltipChildrenProps) => (
          <IconButton
            onBlur={onBlur}
            priority={moreActionsProps?.priority || 'secondary'}
            skin={moreActionsProps?.skin}
            className={st(classes.action, { size, iconAction: true })}
            dataHook={DATA_HOOKS.triggerElement}
            size={size}
            onClick={toggle}
            ref={ref as React.Ref<HTMLButtonElement>}
            ariaLabel={moreActionsTooltipText || undefined}
            aria-haspopup="menu"
            aria-expanded={isOpen}
            onFocus={(
              event: React.FocusEvent,
              { focus }: { focus: () => void },
            ) => {
              onFocus(event, {
                blur: () =>
                  blurElementWithoutFocusVisible(event.target as HTMLElement),
                focus,
              });
            }}
          >
            <icons.More />
          </IconButton>
        )}
      </Tooltip>
    )}
    {...popoverMenuProps}
  >
    {actions.map((action, index) =>
      !('divider' in action && action.divider) ? (
        <PopoverMenu.MenuItem
          skin={
            ((action as TableActionCellSecondaryAction).skin ?? 'standard') as
              | 'standard'
              | 'dark'
              | 'destructive'
          }
          key={index}
          dataHook={
            (action as TableActionCellSecondaryAction).dataHook ||
            DATA_HOOKS.popoverMenuItem
          }
          prefixIcon={(action as TableActionCellSecondaryAction).icon}
          onClick={() => (action as TableActionCellSecondaryAction).onClick()}
          text={(action as TableActionCellSecondaryAction).text}
          disabled={(action as TableActionCellSecondaryAction).disabled}
          disabledDescription={
            (action as TableActionCellSecondaryAction).disabledDescription
          }
          subtitle={(action as TableActionCellSecondaryAction).subtitle}
        />
      ) : (
        <PopoverMenu.Divider />
      ),
    )}
  </PopoverMenu>
);

const renderPlaceholder = (
  size: TableActionCellSize,
  icons: TableActionCellIcons,
) => (
  <IconButton
    dataHook={DATA_HOOKS.placeholder}
    className={classes.action}
    size={size}
    priority="secondary"
    tabIndex={-1}
  >
    <icons.ChevronRight />
  </IconButton>
);

const TableActionCell = (componentProps: TableActionCellProps) => {
  const {
    onClick: _onClick = () => {},
    dataHook,
    primaryAction,
    secondaryActions = [],
    numOfVisibleSecondaryActions = 0,
    alwaysShowSecondaryActions = false,
    alwaysShowPrimaryActions = false,
    moreActionsTooltipText,
    moreActionsProps,
    secondaryActionsProps,
    popoverMenuProps,
    size = 'medium',
  } = useDefaultProps('TableActionCell', componentProps);

  const icons = useIcons('TableActionCell', {
    ChevronRight,
    More,
  });

  const primaryActionSkin =
    primaryAction && !Array.isArray(primaryAction)
      ? (primaryAction as TableActionCellPrimaryAction).skin
      : undefined;

  useEffect(() => {
    if (primaryActionSkin) {
      deprecationLog(
        '<TableActionCell/> - prop "primaryAction" with key "skin" is deprecated and will be removed in next major release. It has been replaced by the visibility property, which switches skin properties automatically based on visibility options.',
      );
    }
  }, [primaryActionSkin]);

  const visibleActions = secondaryActions.slice(
    0,
    numOfVisibleSecondaryActions,
  ) as TableActionCellSecondaryAction[];
  const hiddenActions = secondaryActions.slice(numOfVisibleSecondaryActions);

  const isPrimaryActionArray = Array.isArray(primaryAction);
  const primaryActionDisplay = isPrimaryActionArray
    ? alwaysShowPrimaryActions
      ? 'always'
      : 'onHover'
    : ((primaryAction as TableActionCellPrimaryAction)?.visibility ??
      'onHover');

  const shouldShowPlaceholder = isPrimaryActionArray
    ? !alwaysShowPrimaryActions
    : (primaryAction as TableActionCellPrimaryAction)?.visibility !== 'always';

  return (
    <span
      data-hook={dataHook}
      className={st(classes.root, { size })}
      data-more-actions-tooltip-text={moreActionsTooltipText}
    >
      {primaryAction && (
        <HoverSlot
          data-hook={DATA_HOOKS.primaryActionWrapper}
          display={primaryActionDisplay}
        >
          {isPrimaryActionArray
            ? (primaryAction as TableActionCellMainAction[]).map(action =>
                renderMainActionButton(
                  action as unknown as PrimaryActionButtonConfig,
                  size,
                ),
              )
            : renderMainActionButton(
                primaryAction as unknown as PrimaryActionButtonConfig,
                size,
              )}
        </HoverSlot>
      )}

      {visibleActions.length > 0 && (
        <HoverSlot
          data-hook={DATA_HOOKS.visibleActionsWrapper}
          display={alwaysShowSecondaryActions ? 'always' : 'onHover'}
          className={classes.secondaryActions}
        >
          {renderVisibleActions(visibleActions, size, secondaryActionsProps)}
        </HoverSlot>
      )}

      {hiddenActions.length > 0 && (
        <div className={classes.moreActions} onClick={e => e.stopPropagation()}>
          <HoverSlot display="always">
            {renderHiddenActions(
              hiddenActions,
              popoverMenuProps,
              size,
              moreActionsTooltipText,
              moreActionsProps,
              icons,
            )}
          </HoverSlot>
        </div>
      )}

      {primaryAction &&
        shouldShowPlaceholder &&
        (!secondaryActions.length ||
          secondaryActions.length === numOfVisibleSecondaryActions) && (
          <span className={classes.placeholderIcon} data-display="notOnHover">
            {renderPlaceholder(size, icons)}
          </span>
        )}
    </span>
  );
};

TableActionCell.displayName = 'TableActionCell';

export { TableActionCell };
